import {createContext, useContext, useEffect} from "react";
import {EventEmitter} from "nate-react-api-helpers";
import {PriceLine2} from "../../../../api/Pricing";
import useWindowSize from "react-use/lib/useWindowSize";
import {grey, red} from "@mui/material/colors";
import {IconButton, useTheme} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {lookupNestedProp, Table} from "../../../../misc/scroller/Table";
import {commonPricingColumns3} from "./usePricingKit2";
import {useUser} from "../../../../misc/Permission";
import {useProjectId} from "../ProjectName";
import {priceTableName} from "./Pricing";
import {getAll} from "../../../logistics/routes/useGetAll";
import {api} from "../../../../api/API";
import {useChangesetId} from "../changeset/ChangesetId";

export function SidePricingPanel(props: {
    preColumns?: any[];
    postColumns?: any[];
    kind: "door-hm" | "frame-hm" | "door-wd" | "hardware" | "div10" | "frame-anchor";
}) {
    const {emitter} = useContext(SidePricingContext);
    const value = EventEmitter.reactValue(emitter);
    const win = useWindowSize();
    const theme = useTheme();
    const u = useUser();
    const project = useProjectId();
    const changeset = useChangesetId();

    useEffect(() => {
        return () => {
            if(emitter.lastValue) {
                emitter.emit(null);
            }
        }
    }, [emitter]);

    if(!value) return null;

    const openingId = value.opening;
    const changeSetId = changeset;

    return (
        <div style={{
            width: win.width * 0.33,
            borderLeft: "1px solid " + grey["400"],
            display: "flex",
            flexDirection: "column",
        }}>
            <div style={{
                paddingLeft: 16,
                paddingRight: 16,
                paddingTop: 4,
                paddingBottom: 4,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
            }}>
                <div style={{fontSize: "0.9rem"}}>
                    Related for {value.openingName}
                </div>
                <IconButton onClick={() => emitter.emit(null)} color="inherit" size="small">
                    <CloseIcon color="inherit" />
                </IconButton>
            </div>

            <Table<PriceLine2>
                  name={priceTableName(project, props.kind) + "-sidebar"}
                  cellCustomize={{
                      backgroundColor: row => {
                          if(row.removedInChangesetFlag) {
                              return red["50"]
                          }

                          return undefined;
                      }
                  }}
                  border="1px solid transparent"
                 columns={[
                     ...(props.preColumns || []),
                     ...commonPricingColumns3(u, "", null, true),
                     ...(props.postColumns || []),
                 ].filter(o => !!o).map(o => Object.assign({}, o, {
                     editKey: undefined,
                     editable: undefined,
                     render: o.render || ((dt: any) => {
                         if(o.editKey) return lookupNestedProp(dt, o.editKey)
                         const editKey = o.editableFunc(dt).editKey;
                         if(editKey) return lookupNestedProp(dt, editKey)
                         return "";
                     }),
                 }))}
                 columnDeps={[u]}
                  fetch={async (ctx) => getAll(ctx, offset =>
                      api.pricing.list({
                          project: project, offset,
                          category: props.kind as any,
                          sidebarForChangeset: changeSetId,
                          opening: openingId,
                      }), {
                          useLazy: true,
                      }
                  )}
                 fetchDeps={[project, props.kind, openingId]}
            />
        </div>
    );
}

export function SidePricingPanelOnFocus(props: {
    anchorEl: any;
    row: PriceLine2;
}) {
    const {emitter} = useContext(SidePricingContext);

    useEffect(() => {
        emitter.emit(props.row);
    }, [emitter, props.row]);

    return null;
}

export const SidePricingContext = createContext({
    emitter: new EventEmitter<PriceLine2|null>(),
});